// ⚠️ FICHIER ENGENDRÉ — NE PAS ÉDITER À LA MAIN.
// Source : lib/operateur-leviers.js + lib/leviers/*.js
// Régénérer : node scripts/generer-miroir-leviers.js
// Pourquoi ce doublon : CRA/webpack refuse les imports hors de src/ (Jest, lui,
// les accepte — d'où le risque de tests verts sur un build cassé).
// lib/leviers/logements.js — LES LEVIERS DE LA FICHE LOGEMENT ET DE SES
// RÉGLAGES ATTENANTS (galerie photo, politiques d'annulation, divulgation).
//
// Famille écrite le 2026-09-09, sur le modèle de `lib/leviers/operateur.js`.
// Périmètre reçu : logement_gallery_* · cancellation_policies_* ·
// divulgation_regle_clear · services_orchestrateur_grant/hourly_refuse/retract.
// (`veille_silence` et `divulgation_regle_set` sont DÉJÀ déclarés dans
// `lib/leviers/operateur.js` — ils ne sont pas répétés ici.)

// ══ « vise=logement » AU CATALOGUE — LE NOM DIT, PAS L'IDENTIFIANT ═════════
//
// Les leviers de cette famille qui portent `args_resolus` ne montrent plus
// `logement_id` au modèle : il pose « Cocon Naturel » dans `vise`, et le
// serveur retrouve `lc1` au moment de composer la carte
// (`lib/reperes-geste.js`). L'argument RESTE dans `args_autorises` — l'en
// retirer ferait refuser la carte au clic (`filtrerArgs`, rejoué par le
// navigateur) et casserait les aperçus qui le citent en `depend_de`.
//
// ⚠️ ET SI LE MODÈLE LE COMPOSE QUAND MÊME, LA CARTE NE VIENT PAS. On ne lui
// demande pas de s'abstenir : on rend la chose sans effet, et l'écran demande
// à Dimitri de nommer le logement. « Cocon » tout seul désigne DEUX logements
// aux commissions différentes — ce n'est pas un détail de confort.
const LEVIERS_LOGEMENTS = Object.freeze({
  // ══ GALERIE PHOTO DU LOGEMENT ═════════════════════════════════════════════
  //
  // `logement_gallery_upsert` N'EST PAS DÉCLARÉ ICI, et c'est un choix, pas un
  // oubli :
  //  1. ⚠️ CET ARGUMENT EST PÉRIMÉ, ET ON LE LAISSE VISIBLE POUR QUE PERSONNE NE
  //     LE REFASSE (relu le 2026-09-12). Il disait : « le handler fait deux
  //     choses selon la méthode HTTP et ce registre n'a nulle part où poser la
  //     méthode ». C'était vrai ; ça ne l'est plus depuis que `_forme.js` porte
  //     `methode: "POST"|"PATCH"` (né du bouton mort de `livret_config_publier`).
  //     Un levier PATCH « renommer une photo de la galerie » serait donc
  //     exécutable aujourd'hui — il reste à écrire, ce n'est pas un refus.
  //     Ce qui SUIT, en revanche, tient toujours, et c'est ce qui écarte la
  //     face POST (la création).
  //  2. La création RÉELLE suppose qu'une photo ait déjà été mise en Storage
  //     (signed URL + PUT, deux étapes que fait le NAVIGATEUR AVANT d'appeler
  //     cette action) — l'opérateur ne détient aucun fichier image à uploader.
  //     Composer l'intention reviendrait à faire accepter au serveur un
  //     `public_url` https ARBITRAIRE (le handler ne vérifie que la forme de
  //     l'URL, jamais qu'elle pointe vers notre Storage) : ça n'est pas un
  //     geste qui répond à un fait, c'est une porte ouverte sur du contenu
  //     qu'on ne contrôle pas. Aucune des cinq exclusions ne le nomme
  //     exactement, mais l'esprit d'E2 (rien dont l'effet n'est pas fiable)
  //     s'applique : on ne sait pas dire à Dimitri CE QUI apparaîtra.
  // ══ LA COUVERTURE — FERMÉE LE 2026-09-22, ET LA RAISON RESTE ICI ═════════
  //
  // `logement_gallery_set_cover` A ÉTÉ DÉCLARÉ ICI, EN VAGUE 1, ET IL EST
  // MAINTENANT ÉCARTÉ (`hors_atteinte`, lib/operateur-leviers.js). Le motif
  // n'est pas le risque — ce geste n'en portait aucun — c'est qu'il ne pouvait
  // JAMAIS être composé : il exige `id`, l'identifiant d'une ligne de galerie,
  // et RIEN ne le rend. La lecture `logement_gallery_lire` sert des rangs et
  // un drapeau de couverture, jamais un identifiant ; les sept photos de lc1
  // portent toutes `order_index = 0`, donc même le rang se refuse. Un levier
  // ouvert qui ne peut jamais s'armer est un mensonge de catalogue : il occupe
  // une ligne du catalogue, le modèle le propose, et la carte ne vient pas.
  //
  // ⚠️ CE QUI LE RAMÈNERA : un repère qui DÉSIGNE une photo — le rang, une
  // fois qu'une galerie aura été rangée (`ordre_enregistre: true`), avec la
  // garde d'empreinte de `lib/rang-reperes.js`. Ce jour-là, on le retire
  // d'ECARTES en le disant. Pas avant : sans repère, le rouvrir referait
  // exactement le bouton mort qu'on ferme aujourd'hui.
  //
  // ⚠️ ET SON JUMEAU `logement_gallery_delete` RESTE DÉCLARÉ, en vague 2 —
  // fermée. Il porte le même identifiant inatteignable, mais il n'est pas
  // OUVERT : il ne ment à personne. Le fermer aussi effacerait la différence
  // entre « pas encore ouvert » et « écarté », que Dimitri tient.

  logement_gallery_delete: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "logement_gallery_delete",
    args_autorises: ["id", "logement_id"],
    // Même lecture que la couverture — et c'est ici qu'elle compte le plus :
    // la carte dit « photo 5 sur 8 », et elle dit « c'est la couverture
    // actuelle » quand c'est le cas. C'est la seule chose que Dimitri ne
    // pouvait pas deviner d'un identifiant avant de supprimer un fichier.
    // (L'écriture vérifie le jeton quand il est présent — toujours sur le
    // chemin opérateur, jamais sur celui de l'écran admin : cf. le commentaire
    // du geste de couverture. Ici c'est un FICHIER qui part, sans retour.)
    apercu: {
      lecture: "logement_gallery_lire",
      depend_de: ["logement_id", "id"],
      jeton: "x-apercu-version",
    },
    titre: "Supprimer une photo de la galerie",
    constatable:
      "la photo disparaît de la galerie du logement ET de la page publique que voit le voyageur, et le fichier lui-même est effacé — si vous ne l'avez plus ailleurs, elle n'est pas récupérable : il faudra la reprendre",
    sujet: "fiche",
    onglet: "site",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour:
      "le fichier part du Storage : si Dimitri ne l'a pas ailleurs, la photo n'est pas récupérable — il faudra la reprendre",
    ne_fait_pas:
      "il ne retire pas la photo des pages déjà servies en cache, et il ne prévient pas : la page publique la perd à sa prochaine régénération",
    dit_comme: [
      "Enlève cette photo de la galerie",
      "Supprime cette image du logement",
      "Cette photo est ratée, retire-la",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
    marquages: ["supprime", "sortant"],
    // ⚠️ TENSION ASSUMÉE AVEC LA LETTRE D'E1 ("DELETE dur, Storage" est cité
    // dans la liste des exclusions). Mais l'inventaire complet (2026-09-08,
    // 58 refus) note que E1 pèse "presque uniquement" sur les push Beds24 et
    // les mouvements Stripe — pas sur la suppression d'un contenu éditorial
    // que Dimitri a lui-même posé. Le marquage `supprime` existe précisément
    // pour cette famille de gestes : la carte DOIT nommer ce qui part et dire
    // que ce n'est pas re-créable automatiquement — ce que ce `constatable`
    // fait. « Il propose, je clique » protège ici comme ailleurs : Dimitri
    // voit la photo ciblée avant de valider.
  },

  // ══ POLITIQUES D'ANNULATION (onglet Messages) ═════════════════════════════
  //
  // Les trois actions ci-dessous vivent sous `src/tabs/messages/…` (montées
  // dans `MessagesPanel`) — l'écran où Dimitri CONSTATE est donc « messages »,
  // même si la politique s'affecte ensuite à un logement.
  cancellation_policies_save: {
    ecran: "messages",
    endpoint: "/api/beds24-mapping",
    action: "cancellation_policies_save",
    // ⚠️ `args_autorises` NE PORTE PAS `policy` — c'est volontaire, pas un
    // oubli. Le mode `set` du handler attend `body.policy` = un OBJET
    // ({text_fr, text_en?, text_es?, label?}) : `operateur-geste.js` rejette
    // tout argument qui n'est pas chaîne/nombre/booléen, donc un objet ne se
    // compose JAMAIS ici. Sans `policy`, `op:'set'` échoue proprement en 400
    // (`policy_invalide`) — inoffensif. Restreindre aux deux clés ci-dessous
    // borne donc CE LEVIER au SEUL mode utile qu'il peut vraiment composer :
    // `op:'reset'`, qui retire l'override et rend le texte par défaut codé.
    args_autorises: ["key", "op"],
    // ⚠️ LA CLÉ RESTE UNE LISTE FERMÉE (`args_valeurs` ci-dessous), ET ELLE SE
    // RÉSOUT QUAND MÊME PAR LE NOM. Les deux ne se contredisent pas : le
    // résolveur compare ce que Dimitri dit aux QUATRE clés du code, et rend
    // une absence dite plutôt qu'un à-peu-près. Mesuré : « l'annulation
    // souple » ne résout RIEN — « souple » n'est pas « flexible », et un
    // synonyme faux est une erreur silencieuse (décision D1).
    args_resolus: {
      key: { objet: "politique_annulation", par: ["nom"] },
    },
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur. Sans elles, le
    // modèle compose du plausible — il l'a fait le 2026-09-09 en prod
    // (« adresse » pour « adresse_complete ») — et la carte s'affiche
    // avant d'échouer sous le clic.
    args_valeurs: {
      key: ["flexible", "moderee", "stricte", "non_remboursable"],
      op: ["reset"],
    },
    // ⚠️ L'APERÇU MONTRE CE QU'ON PERD. `sans_retour` dit que le texte
    // personnalisé ne revient pas ; la lecture le met sous les yeux (son début,
    // et le nombre de logements qui s'en servent) AVANT le clic. Un
    // avertissement sans la chose qu'il désigne se lit sans se comprendre.
    // (L'écriture n'exige pas le jeton : action partagée avec l'éditeur admin,
    // qui ne le présente pas.)
    apercu: {
      lecture: "cancellation_policies_lire",
      depend_de: ["key"],
      jeton: "x-apercu-version",
    },
    titre: "Remettre une politique d'annulation par défaut",
    constatable:
      "votre version disparaît et le texte redevient celui d'origine — c'est ce texte-là que le voyageur lira désormais au moment de réserver. Possible sur les quatre conditions d'origine seulement, jamais sur une condition que vous avez créée.",
    sujet: "voyageur",
    cout: ["rien"],
    ou_ca_part: ["atteignable"],
    sans_retour:
      "l'override retiré n'est gardé nulle part : le texte personnalisé que Dimitri avait écrit ne revient pas, seul le défaut codé revient",
    ne_fait_pas:
      "il ne touche à aucun contrat déjà signé et n'écrit aucun texte : il RETIRE une surcharge pour rendre le texte d'origine. Il ne sait pas non plus en poser une — le mode « écrire » ne se compose pas ici.",
    dit_comme: [
      "Remets la condition d'annulation d'origine",
      "Annule ce que j'avais réécrit sur cette condition",
      "Reviens au texte standard pour l'annulation souple",
    ],
    marquages: ["sortant"],
    vague: 2,
    motif_requis: false,
    autonome: false,
    // E1 ✔ (le texte par défaut est un CONSTANT connu, donc réversible :
    // reposer l'ancien override réel n'est PAS automatique, mais le défaut
    // n'est jamais perdu, lui) · E2 ✔ · E3 ✔ (écrivain unique du blob KV) ·
    // E4 ✔ · E5 ✔.
  },

  cancellation_policies_create: {
    ecran: "messages",
    endpoint: "/api/beds24-mapping",
    action: "cancellation_policies_create",
    args_autorises: ["label", "text_fr"],
    // La lecture dit ce qui EXISTE DÉJÀ — et nommément si une politique porte
    // déjà ce nom. C'est le seul risque de ce geste : un doublon de plus dans
    // une liste que Dimitri affecte ensuite à des logements.
    apercu: {
      lecture: "cancellation_policies_lire",
      depend_de: ["label"],
      jeton: "x-apercu-version",
    },
    titre: "Créer une politique d'annulation",
    constatable:
      "la nouvelle politique apparaît dans la liste de l'éditeur, prête à être affectée à un logement",
    // ── VAGUE 9 — texte de la main de Dimitri. `text_fr` est jusqu'à 2000
    // caractères de clause contractuelle réelle (Hoguet-sensible) : ce n'est
    // pas un identifiant, c'est un texte qu'il doit écrire ou relire mot à
    // mot avant que ça parte dans un contrat. Décrit, pas exclu (même
    // raisonnement que `menage_instruction_save`/`inbox_ia_faq_create`).
    sujet: "voyageur",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour: null, // effaçable ensuite par `cancellation_policies_delete`
    ne_fait_pas:
      "il ne l'affecte à aucun logement et ne change aucune réservation : la politique naît disponible, et reste sans effet tant que personne ne l'affecte",
    dit_comme: [
      "Crée une condition d'annulation à moi",
      "Il me faut une règle d'annulation particulière",
      "Ajoute une nouvelle condition d'annulation",
    ],
    vague: 9,
    motif_requis: false,
    autonome: false,
    // E1 ✔ (créer une clé custom, effaçable ensuite par
    // `cancellation_policies_delete`) · E2 ✔ · E3 ✔ · E4 ✔ · E5 ✔.
  },

  cancellation_policies_delete: {
    ecran: "messages",
    endpoint: "/api/beds24-mapping",
    action: "cancellation_policies_delete",
    args_autorises: ["key"],
    // ⚠️ `key` N'EST PAS UNE LISTE FERMÉE ICI, ET LE CONTRÔLE AUTOMATIQUE LE
    // CROIT — d'où cette déclaration explicite. Le handler cite bien
    // `POLICY_ENUMS.includes(key)`, mais pour REFUSER : les quatre politiques
    // d'origine ne se suppriment pas. Ce qui est accepté, ce sont les clés
    // `custom_*`, créées à la volée — une liste qu'on ne peut pas énumérer,
    // parce qu'elle n'existe qu'en base.
    // On l'écrit plutôt que de faire taire le test : une exception muette
    // rendrait invisible le jour où cette clé DEVIENDRAIT énumérable.
    args_libres: ["key"],
    // ⚠️ C'EST ICI QUE LA LECTURE COMPTE LE PLUS. Elle rend le texte qu'on
    // efface ET le nombre de logements qui s'en servent — le fait qui décide
    // du refus (409 `politique_affectee`). Jusqu'ici, le serveur opposait ses
    // deux refus APRÈS le clic.
    apercu: {
      lecture: "cancellation_policies_lire",
      depend_de: ["key"],
      jeton: "x-apercu-version",
    },
    titre: "Supprimer une politique d'annulation",
    constatable:
      "la condition disparaît de la liste — le geste est refusé net sur les quatre conditions d'origine, et refusé aussi tant qu'au moins un logement s'en sert encore ; passé ces deux barrières, le texte que vous aviez écrit n'est PAS récupérable",
    sujet: "voyageur",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour:
      "le texte personnalisé n'est gardé nulle part : passé les deux refus du serveur, il n'est pas récupérable",
    ne_fait_pas:
      "il ne touche pas aux quatre conditions d'origine, et ne peut pas retirer une condition dont un logement se sert encore : aucun logement ne se retrouve donc sans condition d'annulation après ce clic",
    dit_comme: [
      "Supprime cette condition d'annulation que j'avais créée",
      "Enlève cette règle d'annulation, je ne m'en sers plus",
      "Retire ma condition personnalisée",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
    marquages: ["supprime"],
    // Même tension assumée qu'avec `logement_gallery_delete` : DELETE dur au
    // sens littéral d'E1, mais borné par le handler lui-même à un cas où RIEN
    // n'en dépend (409 sinon) et où ce n'est jamais l'une des 4 politiques
    // socle. Le `constatable` porte les deux gardes ET l'irréversibilité —
    // c'est ce que le marquage `supprime` exige.
  },

  // ══ DIVULGATION AU COPILOTE — LE GESTE INVERSE ════════════════════════════
  //
  // `divulgation_regle_set` (déjà déclaré dans `lib/leviers/operateur.js`)
  // NE SAIT QUE RESTREINDRE. `divulgation_regle_clear` va dans l'AUTRE sens :
  // il retire une règle posée, et rend la main au PLAFOND câblé en dur dans
  // `lib/divulgation-resolve.js` — jamais plus bas. Ce n'est donc pas une
  // vraie perte de contenu (rien à re-créer : le plafond, lui, ne bouge
  // jamais), mais c'est un geste qui ROUVRE une porte au copilote, et le
  // `constatable` doit le dire sans détour.
  divulgation_regle_clear: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "divulgation_regle_clear",
    args_autorises: ["logementId", "champ", "portee_type"],
    // Mêmes listes fermées que `divulgation_regle_set` — un geste inverse qui
    // ne connaîtrait pas les mêmes valeurs que l'aller poserait la restriction
    // et ne saurait pas la lever.
    args_valeurs: {
      champ: [
        "wifi_code",
        "box_code",
        "door_code",
        "adresse_complete",
        "instructions_acces_batiment",
      ],
      portee_type: ["logement", "partout"],
    },
    // ⚠️ L'APERÇU SUR LE GESTE QUI DESSERRE. La carte lit les règles posées sur
    // ce logement, dit laquelle porte sur ce champ, depuis quand, et rappelle
    // le PLAFOND qui, lui, ne bouge jamais. Lever une restriction sans voir
    // laquelle était le manque qui comptait ici.
    //
    // ⚠️ MÊME LECTURE QUE LE GESTE INVERSE (`divulgation_regle_set`, déclaré
    // dans lib/leviers/operateur.js) : `divulgation_regles_lire`
    // (lib/divulgation-handlers.js), qui rend `{ regles: [{ champ, portee,
    // exigences, motif, quand }], visee, plafond, absent }`. Deux lectures
    // jumelles sous deux noms auraient divergé au premier ajout.
    //
    // ⚠️ `portee_type` EST DANS `depend_de` (revue adverse du 2026-09-16) : une
    // règle sur lc1 ET une « partout » sur le même champ, un geste qui part
    // avec `portee_type: "partout"` — sans la portée, la carte montrait celle
    // du logement et le clic levait la globale. Sur le seul levier qui
    // DESSERRE le copilote, la carte doit viser exactement ce que le clic
    // retire.
    //
    // (L'écriture n'exige pas le jeton : action partagée avec l'écran admin.)
    apercu: {
      lecture: "divulgation_regles_lire",
      depend_de: ["logementId", "champ", "portee_type"],
      jeton: "x-apercu-version",
    },
    titre: "Lever une restriction du copilote",
    constatable:
      "la règle disparaît de la fiche du logement — le copilote redevient aussi bavard sur ce champ que le PLAFOND le permet (jamais plus, mais plus qu'avant ce clic)",
    sujet: "messages",
    onglet: "savoir",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour: null, // `divulgation_regle_set` repose la restriction
    // ⚠️ C'EST LE SENS QUI DESSERRE — le seul des deux.
    ne_fait_pas:
      "il ne rend pas le copilote bavard : il lève UNE restriction, et le plafond (la table, le scrubber, le contexte) continue de tenir. Mais c'est le geste de cette paire qui va dans le sens le moins prudent.",
    dit_comme: [
      "Il peut redonner le code wifi du Cocon",
      "Enlève la restriction que j'avais mise",
      "Laisse-le redire l'adresse complète",
    ],
    vague: 2,
    // ⚠️ `motif_requis: true` alors que le geste jumeau (`set`) ne l'exige
    // pas : poser une restriction est un choix prudent par construction (la
    // table ne sait QUE restreindre), la retirer ne l'est pas — on demande
    // donc ici, et seulement ici, que Dimitri écrive pourquoi avant de
    // cliquer. Le champ n'est pas transmis au serveur (le handler ne lit pas
    // de motif pour `clear`) : c'est une trace pour Dimitri, pas pour la base.
    // ⚠️ `false` DEPUIS LE 2026-09-09, et c'est une correction. Ce levier
    // exigeait un motif que le handler NE LIT PAS : la carte l'imposait à
    // Dimitri, et son texte partait dans le vide. Une friction sans effet use
    // la confiance dans celles qui en ont un — les motifs qui comptent
    // (taire une alerte, lever une reprise, débloquer un livret) sont
    // enregistrés, eux.
    motif_requis: false,
    autonome: false,
    // E1 ✔ (DELETE d'une ligne de POLITIQUE, pas de contenu voyageur ou
    // d'argent — et le plafond, filet de sécurité, ne bouge jamais) · E2 ✔
    // · E3 ✔ (écrivain unique de `divulgation_regles`) · E4 ✔ · E5 ✔.
  },

  // ══ HUB SERVICES HORAIRES (écran « Horaires », catégorie Aujourd'hui) ═════
  //
  // `services_orchestrateur_grant` et `services_orchestrateur_hourly_refuse`
  // NE SONT PAS DÉCLARÉS — les deux sont explicitement commentés CHEMIN
  // ARGENT dans `lib/orchestrateur-handlers.js` :
  //  - `grant` fait une CAPTURE PARTIELLE Stripe réelle (montant recalculé
  //    serveur, mais l'argent bouge VRAIMENT, immédiatement, chez Stripe) ;
  //  - `hourly_refuse` fait un RELEASE TOTAL du hold — irréversible dans
  //    l'autre sens (on ne peut plus reprendre une autorisation relâchée).
  // C'est exactement la même famille que `capture_caution`/`resolve_caution`
  // dans `INTERDITS_ABSOLUS` : « un mot tapé protège d'un faux clic, pas
  // d'une prémisse fausse ». E1 (« argent ») s'applique à la lettre, sans la
  // nuance appliquée plus haut aux suppressions de contenu — ici c'est un
  // vrai mouvement de fonds, jamais un candidat à la clique-pour-proposer.
  services_orchestrateur_hourly_retract: {
    ecran: "orchestrateur-horaires",
    endpoint: "/api/beds24-mapping",
    action: "services_orchestrateur_hourly_retract",
    args_autorises: ["booking_id"],
    titre: "Retirer une proposition d'horaire",
    constatable:
      "la proposition disparaît de la file « Demandes d'horaires » et redevient proposable (par l'orchestrateur ou par ce même levier plus tard)",
    sujet: "terrain",
    cout: ["rien"],
    // La proposition n'avait pas encore été validée : rien n'était parti chez
    // le voyageur, et rien ne part en la retirant.
    ou_ca_part: ["personne"],
    sans_retour: null, // la demande redevient proposable, par le même chemin
    ne_fait_pas:
      "il ne prévient pas le voyageur et ne refuse pas sa demande : il retire une proposition EN ATTENTE, qui redevient proposable. Aucun euro ne bouge — ni capture, ni libération chez Stripe.",
    apercu_absent:
      "aucune lecture ne rend la proposition (quel créneau, pour quel séjour) : il faudrait `services_hourly_lire`. La carte ne porte qu'un identifiant de réservation.",
    dit_comme: [
      "Retire la proposition d'horaire que j'ai faite",
      "Annule le créneau que j'avais proposé",
      "Reprends cette proposition d'heure",
    ],
    vague: 1,
    motif_requis: false,
    autonome: false,
    // ⚠️ CE N'EST PAS L'OPÉRATEUR (l'IA) QUI RETIRE SA PROPRE PROPOSITION —
    // E5 ne s'applique pas : `orchestrateur` ici est un RÔLE HUMAIN (staff),
    // sans rapport avec le copilote. Le handler ne fait bouger AUCUN euro
    // (`proposed_*` → NULL, jamais après une décision admin : `validated_at`
    // gardé NULL, la ligne doit encore être `pending_validation`). Écrivain
    // unique de ces 4 colonnes en dehors de la proposition initiale, qui est
    // précisément son inverse. E1 ✔ (0 argent, 0 Storage) · E2 ✔ (la file se
    // vide à l'écran) · E3 ✔ · E4 ✔ · E5 ✔.
  },

  // ══ ACTIONS DE PILOTAGE (2026-09-13) — CE QUI MANQUAIT POUR AGIR ════════
  //
  // Dimitri : « On ne bute pas, on corrige. Si une action n'existe pas côté
  // serveur, crée-la. […] Pas un écran à ouvrir, pas un bouton mort. » Quatre
  // gestes montés ce jour, chacun avec SON action serveur (aucune n'existait
  // sur ce routeur).
  //
  // ⚠️ CONSIGNE DU JOUR : « tant que je ne me sers pas vraiment de l'app, tout
  // ce qui dépend de ma saisie quotidienne est muet par construction ». Ces
  // leviers existent et sont testés sur des mocks MUTABLES ; aucun n'a été
  // validé sur une donnée réelle — 5 tâches échues, 0 service au catalogue.

  admin_task_done: {
    ecran: "mes-taches",
    endpoint: "/api/beds24-mapping",
    action: "admin_task_done",
    args_autorises: ["id"],
    // ⚠️ L'IDENTIFIANT D'UNE TÂCHE N'EST RENDU PAR AUCUNE LECTURE — c'est ce
    // que dit `apercu_absent` ci-dessous, et c'était jusqu'ici un geste à
    // l'aveugle sur l'écran le plus utilisé. Dimitri dit « la tâche du contrat
    // Fabien » ; le résolveur retrouve la ligne, et une tâche DÉJÀ cochée
    // remonte en le disant au lieu de disparaître.
    //
    // ⚠️ PAS DE REPÈRE `logement` : les six tâches du parc portent
    // `logement_id` NULL. Le déclarer promettrait un départage que la base ne
    // peut pas rendre.
    args_resolus: {
      id: { objet: "tache", par: ["nom"] },
    },
    titre: "Cocher une tâche comme faite",
    constatable:
      "la tâche passe dans la colonne « Fait » du tableau Mes tâches, datée de l'instant du clic — elle se remet en « À faire » d'un glisser dans le tableau",
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour: null, // se remet en « À faire » d'un glisser dans le tableau
    ne_fait_pas:
      "il ne supprime pas la tâche et ne prévient personne : elle change de colonne, datée de l'instant du clic",
    apercu_absent:
      "aucune lecture ne rend le libellé de la tâche : il faudrait `admin_task_lire`. Cocher un identifiant sans lire ce qu'il désigne est le défaut de cette carte — et c'est l'écran le plus utilisé au quotidien.",
    dit_comme: [
      "Coche cette tâche, c'est fait",
      "Marque cette tâche comme terminée",
      "J'ai fait ça, range-le",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
    // E1 ✔ (statut + date, réversible dans le kanban) · E2 ✔ · E3 ✔ (même
    // couple status/done_at que le client, lib/taches-handlers.js) · E4 ✔
    // (cocher = constater que c'est fait, mais c'est Dimitri qui clique, pas
    // l'opérateur qui atteste) · E5 ✔.
  },

  webhook_replay: {
    ecran: "params",
    endpoint: "/api/beds24-mapping",
    action: "webhook_replay",
    args_autorises: ["id"],
    titre: "Retraiter un message de Beds24 non passé",
    constatable:
      "la réservation que ce message annonçait apparaît (ou se met à jour) dans vos réservations, et la ligne passe de « échec » à « traité » dans Paramètres › Sync Beds24 — refusé si le message ne vient pas de Beds24, s'il a déjà été traité, ou si un message plus récent l'a dépassé",
    sujet: "reglages",
    // Aucun appel sortant : le payload est DÉJÀ chez nous, on le rejoue
    // localement. Stripe est refusé en 403, et rien ne repart vers Beds24.
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour:
      "la ligne passe en « traité » et ne se retraite plus : la réservation qu'elle annonce est écrite chez vous telle que Beds24 la décrivait ce jour-là",
    ne_fait_pas:
      "il ne redemande RIEN à Beds24 et ne leur envoie rien : il relit un message déjà reçu. Si un message plus récent l'a dépassé, le geste est refusé — le passé ne réécrit pas le présent.",
    apercu_absent:
      "aucune lecture ne rend le contenu du message en échec (quelle réservation, quelles dates, quelle erreur) : il faudrait `evenement_entrant_lire`. Retraiter sans savoir ce qu'on retraite est possible aujourd'hui.",
    dit_comme: [
      "Une réservation de Beds24 n'est pas arrivée, retraite-la",
      "Ce message de Beds24 est en erreur, relance-le",
      "Il manque une réservation, essaie de la reprendre",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
    // ⚠️ SOURCE FIABLE ? Pas entièrement — dit franchement. Le handler
    // (lib/beds24-mapping-handlers.js::handleWebhookReplay) documente
    // l'incohérence trouvée avant de le monter : 17 lignes `status='processed'`
    // SANS `processed_at`, écrites par le cron polling (étape 4d) qui ne pose
    // pas la date. `status` fait foi ; le geste ne rejoue QUE `failed`, jamais
    // « processed_at vide ». Un événement qui produirait ce geste doit lire
    // `status`, pas la date.
    // E1 ✔ (écriture LOCALE de reservations, 0 push Beds24, 0 argent — Stripe
    // refusé en 403) · E2 ✔ · E3 ✔ (même mapper que le cron) · E4 ✔ · E5 ✔.
  },

  service_logement_toggle: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "service_logement_toggle",
    args_autorises: ["logement_id", "service_catalog_id", "actif"],
    // Deux identifiants, deux repères : le logement par son nom (lot 0), le
    // service par son titre — un `jsonb` multilingue résolu fr → en → es
    // (`lib/texte-i18n.js`), donc jamais par sa traduction anglaise.
    args_resolus: {
      logement_id: { objet: "logement", par: ["logement"] },
      service_catalog_id: { objet: "service", par: ["nom"] },
    },
    titre: "Activer ou couper un service",
    constatable:
      "le service passe actif (ou inactif) dans Logements › fiche › Services, et le voyageur peut (ou ne peut plus) l'acheter sur la page du logement — son prix et son heure ne bougent pas",
    // Réglage borné : un booléen sur une paire d'identifiants. Il change ce
    // que le voyageur PEUT acheter (pas à quel prix : le prix reste celui du
    // catalogue ou de la surcharge existante, jamais écrits ici).
    sujet: "fiche",
    onglet: "services",
    cout: ["rien"],
    // Il change ce que le voyageur PEUT acheter sur la page du logement.
    ou_ca_part: ["page_publique"],
    sans_retour: null, // un booléen, rebasculable
    ne_fait_pas:
      "il ne touche NI au prix surchargé du service, NI à son heure de gain, et n'annule aucune réservation de service déjà prise : il ouvre ou ferme la possibilité d'en acheter",
    apercu_absent:
      "aucune lecture ne rend l'état du service sur ce logement ni son prix : il faudrait `service_logement_lire`. Basculer un booléen sans savoir de quel côté il est aujourd'hui est un geste à l'aveugle.",
    dit_comme: [
      "Active le service petit-déjeuner sur ce logement",
      "Enlève ce service du Cocon",
      "Propose la location de vélo au Nid",
    ],
    marquages: ["sortant"],
    vague: 4,
    motif_requis: false,
    autonome: false,
    // ⚠️ Dimitri, 2026-09-13 : « le catalogue est vide, monte le geste quand
    // même. Je le remplirai. » (1 ligne de catalogue, 1 liaison inactive en
    // prod ce jour.) Fail-closed sur service inconnu / logement hors config.
    // E1 ✔ (booléen, réversible) · E2 ✔ · E3 ✔ (n'écrit QUE is_active —
    // contrairement à `services_logement_toggle` de /api/services, qui remet
    // le prix surchargé à null quand on l'omet) · E4 ✔ · E5 ✔.
  },

  logement_horaires_set: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "logement_horaires_set",
    args_autorises: ["logement_id", "checkin", "checkout"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    // ⚠️ PAS d'`args_valeurs` : la grille de l'écran compte 96 valeurs (au
    // quart d'heure, src/tabs/logements/atoms.jsx::generateTimes), au-delà du
    // plafond de 40. Le serveur valide la MÊME grille (HH:MM, minutes ∈
    // {00,15,30,45}) et REFUSE hors grille — jamais d'arrondi.
    titre: "Régler l'heure d'arrivée ou de départ",
    constatable:
      "l'heure change dans Livret › Instructions du logement, et c'est celle que les messages, le contrat et le livret voyageur emploient dès maintenant — l'heure d'une réservation précise n'est jamais touchée",
    // Réglage borné, mais qui atteint le voyageur : l'heure écrite ici est
    // celle qu'il reçoit. Vague 4, pas 2.
    sujet: "fiche",
    onglet: "instr",
    cout: ["rien"],
    // ⚠️ RIEN NE PART AU CLIC, mais cette heure-là est celle que le voyageur
    // recevra : le livret, le contrat et les messages la reprennent.
    ou_ca_part: ["atteignable"],
    sans_retour: null, // deux chaînes, reposables à l'identique
    ne_fait_pas:
      "il ne touche l'heure d'AUCUNE réservation précise, et ne prévient aucun voyageur déjà en séjour : c'est l'heure du LOGEMENT, reprise par les prochains messages et documents",
    apercu_absent:
      "aucune lecture ne rend les horaires actuels du logement : il faudrait les ajouter au catalogue de `logement_champ_lire` (elles n'y sont pas). Poser une heure d'arrivée sans voir celle d'aujourd'hui, c'est décaler à l'aveugle une information que le voyageur lira.",
    dit_comme: [
      "L'arrivée passe à 16 h au Cocon",
      "Change l'heure de départ de ce logement",
      "Départ à 11 h au lieu de 10 h",
    ],
    marquages: ["sortant"],
    vague: 4,
    motif_requis: false,
    autonome: false,
    // ⚠️ ARBITRAGE DIMITRI (2026-09-13), à la lettre : « heure d'arrivée : celle
    // du logement seulement, pas celle d'une réservation ». `reservations.
    // arrival_time` est écrasée par le cron Beds24 (exclue de
    // RESERVATION_SCOPE_B_WHITELIST pour cette raison) — un geste dessus
    // serait défait au passage suivant. Source retenue = le champ Instructions
    // (`checkin_time`/`checkout_time` + miroir `schedule.*`), écrit là où
    // l'écran l'écrit, sous garde de diff intégral
    // (lib/logement-horaires-handlers.js).
    // E1 ✔ (deux chaînes, réversibles) · E2 ✔ · E3 ✔ (même couple plat+miroir
    // que l'écran) · E4 ✔ · E5 ✔.
  },

  // ══ « MODIFIE CE CHAMP DE CETTE FICHE » (2026-09-14) ═════════════════════
  //
  // Dimitri : « Tu dis que la moitié de l'app est hors de portée parce que
  // ces champs n'ont pas d'action serveur. Il n'en faut qu'une : "modifie ce
  // champ de cette fiche". » Une action (`logement_champ_set`), un CATALOGUE
  // FERMÉ de chemins (lib/fiche-champs-catalogue.js) — jamais « n'importe
  // quel champ » : le blob porte la caution, le compte Stripe, la clé
  // Anthropic, et un chemin libre serait le contournement des treize
  // interdits absolus.
  //
  // ⚠️ DEUX LEVIERS SUR LA MÊME ACTION, ET C'EST VOULU. La commission est de
  // l'ARGENT (elle décide de ce que touche un propriétaire) ; le statut ou un
  // code de porte n'en sont pas. Un marquage est porté par un levier entier :
  // un seul levier aurait affiché « ce geste porte un montant » sur un code
  // d'interphone, ou rien sur un taux. Les deux listes `champ` sont RECOPIÉES
  // du catalogue et un test vérifie qu'elles le partitionnent exactement
  // (argent / non-argent) — un champ d'argent glissé dans le levier sans
  // marquage rougit.
  //
  // ⚠️ `nouvelle` (la nouvelle valeur) EST LIBRE PAR CONSTRUCTION, et déclaré
  // tel — pas nommé `valeur` : la sentinelle des arguments d'argent ancre
  // `eur$`, et « valeur » y tombait alors qu'un code de porte n'est pas un
  // montant. Sa forme dépend
  // du champ (un enum pour le statut, un entier pour la capacité, un code
  // pour la porte). Le serveur la valide contre le catalogue et refuse tout
  // ce qui n'y entre pas — jamais d'arrondi, jamais de correction. La CARTE
  // la relit aussi AVANT le clic (aperçu : valeurs fermées rendues par la
  // lecture) et bloque le bouton si elle est hors liste.
  //
  // ⚠️ L'APERÇU : la carte lit `logement_champ_lire` (avec le jeton de
  // Dimitri, jamais le modèle) et affiche le logement, le champ EN FRANÇAIS,
  // l'ancienne valeur, la nouvelle, et où le constater. Elle en reçoit le
  // jeton de version, que l'écriture EXIGE (`x-cfg-version`, 409 si la base a
  // bougé) — le même mécanisme que le bouton Enregistrer, appliqué à un champ.
  logement_champ_set: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "logement_champ_set",
    methode: "POST",
    args_autorises: ["logement_id", "champ", "nouvelle"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    args_valeurs: {
      champ: [
        "statut",
        "capacite_personnes",
        "capacite_chambres",
        "capacite_salles_de_bain",
        "surface_m2",
        "etage",
        "code_boite_cles",
        "code_porte",
        "code_interphone",
        "code_coffre",
        "code_garage",
      ],
    },
    args_libres: ["nouvelle"],
    apercu: {
      lecture: "logement_champ_lire",
      depend_de: ["logement_id", "champ"],
      jeton: "x-cfg-version",
    },
    titre: "Modifier un champ de la fiche du logement",
    constatable:
      "la nouvelle valeur apparaît dans la fiche du logement (Infos pour le statut, Instructions pour la capacité, la surface, l'étage et les codes) — et c'est celle que le livret, le contrat et les messages emploient dès maintenant ; les codes atteignent le voyageur — refusé sur une maison de sous-location : ce réglage se fait chambre par chambre",
    // ⛔ PAS SUR LA MAISON. Le serveur refuse déjà (`logement_parent`) : sans
    // cette phrase, « passe la Maison Zen en inactif » posait la carte et
    // l'aperçu la grisait — le défaut symétrique de la commission (#1480, M2).
    refuse_sur_parent:
      "La {maison} n'est pas un logement qu'on loue : ce réglage se fait " +
      "chambre par chambre — {chambres}. Je n'ai rien préparé.",
    // Réglage borné, mais un code de porte atteint le voyageur : vague 4.
    sujet: "fiche",
    onglet: "instr",
    cout: ["rien"],
    // ⚠️ CINQ DES ONZE CHAMPS SONT DES CODES D'ACCÈS, ET ILS ATTEIGNENT LE
    // VOYAGEUR — le `constatable` le dit déjà en toutes lettres, le marquage
    // `sortant`, lui, ne le dit pas. Le remède n'est pas d'ajouter `sortant`
    // ici (il mentirait sur la surface et l'étage) : un marquage porte sur un
    // levier ENTIER, donc il faut un TROISIÈME levier `logement_code_set` sur
    // la même action — le patron `logement_commission_set`, exactement.
    // Cadrage du 2026-09-15, LOT 0 § 4. Non fait ici : ce lot n'ajoute aucun
    // levier. La destination ci-dessous rend le fait visible en attendant.
    ou_ca_part: ["atteignable"],
    sans_retour: null, // un scalaire, et la carte montre l'ancienne valeur
    ne_fait_pas:
      "il ne touche ni la caution, ni le compte Stripe, ni aucune clé d'API : le catalogue est fermé et ces chemins-là en sont exclus. Et il ne prévient personne — un code changé atteint le voyageur par le livret et les messages, pas par une alerte.",
    dit_comme: [
      "Change le code de la porte du Cocon",
      "Ce logement fait 45 m², corrige-le",
      "Passe ce logement en inactif",
    ],
    marquages: ["sortant"],
    vague: 4,
    motif_requis: false,
    autonome: false,
    // E1 ✔ (un scalaire, refaisable ; la carte montre l'ancienne valeur) ·
    // E2 ✔ · E3 ✔ (même chemin que l'écran, diff intégral, jeton de version)
    // · E4 ✔ · E5 ✔.
  },

  // ══ « LES TEXTES DE LA FICHE » (2026-09-17) ══════════════════════════════
  //
  // ⚠️ UN TROISIÈME LEVIER SUR LA MÊME ACTION, ET C'EST LE MÊME MOTIF QUE LE
  // SECOND. Le commentaire de `logement_champ_set` le demandait déjà en toutes
  // lettres : « un marquage porte sur un levier ENTIER ». Ce qui vaut pour
  // l'argent vaut pour la PROSE — et même davantage.
  //
  // Un nombre de chambres est recopié par un gabarit ; un texte d'accès, une
  // règle de la maison, une adresse sont lus MOT POUR MOT par le voyageur dans
  // son livret, son contrat et ses messages. Les mettre sur la même carte que
  // « la surface fait 45 m² » obligerait un seul `constatable` à décrire les
  // deux — donc à être vrai pour l'un et creux pour l'autre.
  //
  // ⚠️ ET LE `sans_retour` N'EST PAS LE MÊME, CE QUI TRANCHE. Une surface
  // remplacée, la carte montre l'ancienne valeur : deux chiffres, on refait.
  // Un paragraphe de deux mille caractères remplacé, la carte montre l'ancien
  // texte — mais personne ne recopie deux mille caractères depuis une carte.
  // Le geste se refait ; le texte d'avant, lui, n'est gardé nulle part.
  //
  // ⚠️ CE LEVIER N'INVENTE AUCUNE ACTION. Il déclare d'autres champs du MÊME
  // catalogue fermé (`lib/fiche-champs-catalogue.js`) sur la MÊME action
  // (`logement_champ_set`) : deux types s'y ajoutent — `texte_long` (borné,
  // sans caractères de commande) et `lien` (liste blanche http/https, parce
  // que ces deux valeurs finissent en `href`).
  logement_texte_set: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "logement_champ_set",
    methode: "POST",
    args_autorises: ["logement_id", "champ", "nouvelle"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    args_valeurs: {
      champ: [
        "adresse",
        "adresse_precision",
        "acces_immeuble",
        "acces_logement",
        "parking",
        "poubelles",
        "courses",
        "regles_maison",
        "lien_livret",
        "lien_avis_google",
      ],
    },
    args_libres: ["nouvelle"],
    apercu: {
      lecture: "logement_champ_lire",
      depend_de: ["logement_id", "champ"],
      jeton: "x-cfg-version",
    },
    titre: "Récrire un texte de la fiche du logement",
    constatable:
      "le nouveau texte apparaît dans Logements › Instructions, à la carte qui porte son nom — et c'est celui-là que le voyageur lira dans son livret d'accueil, dans son contrat et dans ses prochains messages, dès son prochain affichage. L'ancien texte est REMPLACÉ, pas complété : la carte vous le montre en entier avant le clic. L'adresse, elle, ne peut pas être vidée — un logement sans adresse casse le livret et le trajet du voyageur. Effacer un texte n'est pas anodin : si vous effacez le lien du livret, le bouton « Mon livret » disparaît de la page de tous les voyageurs en séjour, et rien ne vous le signale ensuite ; si vous effacez les règles de la maison, elles disparaissent des messages envoyés aux voyageurs. Dans les deux cas le logement reste en ligne : c'est le voyageur qui perd l'information, pas vous. Refusé sur une maison de sous-location : ce texte se règle chambre par chambre.",
    // ⛔ PAS SUR LA MAISON — même refus serveur (`logement_parent`), même
    // phrase fixe que le champ voisin (#1480, M2).
    refuse_sur_parent:
      "La {maison} n'est pas un logement qu'on loue : ce texte se règle " +
      "chambre par chambre — {chambres}. Je n'ai rien préparé.",
    sujet: "fiche",
    onglet: "instr",
    cout: ["rien"],
    ou_ca_part: ["atteignable"],
    ne_fait_pas:
      "il ne prévient personne : un voyageur déjà sur place verra le nouveau texte à sa prochaine ouverture du livret. Il ne touche ni au Wi-Fi, ni aux horaires, ni aux codes, ni à la page publique.",
    dit_comme: [
      "Le local poubelles a déménagé, corrige le texte",
      "Récris les consignes de parking du Cocon",
      "Change l'adresse de ce logement",
      "Mets à jour les règles de la maison",
    ],
    marquages: ["sortant"],
    vague: 4,
    motif_requis: false,
    autonome: false,
    sans_retour:
      "le texte d'avant n'est gardé nulle part : la carte vous le montre, mais une fois remplacé il faudra le réécrire.",
    // E1 ✔ (le geste se refait ; c'est `sans_retour` qui dit ce qui ne revient
    // pas) · E2 ✔ · E3 ✔ (même chemin que l'écran, diff intégral, jeton de
    // version) · E4 ✔ · E5 ✔.
  },

  logement_commission_set: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "logement_champ_set",
    methode: "POST",
    args_autorises: ["logement_id", "champ", "nouvelle"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    args_valeurs: {
      champ: ["commission_mode", "commission_taux", "commission_taux_base"],
    },
    args_libres: ["nouvelle"],
    // ⚠️ UN TAUX SE TAPE, IL NE SE COMPOSE PAS (lot 6, revue adverse M1 —
    // chemin argent). « Passe la commission à 18 » : 18 % est ce que le
    // propriétaire verra sur sa facture ; le modèle l'entendait et l'écrivait.
    // Désormais retiré s'il est composé, champ sur la carte — SEULEMENT pour
    // les deux champs de taux : le MODE (« fixe », « dégressif ») est une
    // valeur fermée que le modèle compose, et qui reste sans champ à taper.
    args_saisis: {
      nouvelle: {
        libelle: "le taux, en pour cent",
        nature: "nombre",
        si: { champ: ["commission_taux", "commission_taux_base"] },
      },
    },
    apercu: {
      lecture: "logement_champ_lire",
      depend_de: ["logement_id", "champ"],
      jeton: "x-cfg-version",
    },
    titre: "Modifier la commission d'un logement (mode, taux)",
    constatable:
      "le taux (ou le mode) change dans Logements › Infos › Commission, et c'est lui qui calculera le montant en € que le propriétaire verra sur sa prochaine facture — refusé si le mois en cours est déjà facturé pour ce logement, refusé sur une chambre, et refusé sur une maison qui a des chambres : en sous-location il n'y a pas de commission, ni sur la maison ni sur ses chambres",
    sujet: "fiche",
    onglet: "info",
    cout: ["argent"],
    // ⛔ PAS SUR UNE CHAMBRE, ET LE REFUS SE POSE AVANT LA CARTE. La phrase est
    // FIXE (`refusSousLocation`, operateur-geste.js) : ni récupérée du modèle,
    // ni « visez la maison parente ». Poser un taux sur la Maison Zen est
    // ACCEPTÉ par le serveur et lu différemment par deux moteurs (proprio
    // hérite du parent, la facturation admin reste à 0 %) : ce conseil-là
    // coûterait de l'argent. `l8` est à 0 % DÉLIBÉRÉMENT — en sous-location,
    // FBZ ne se prend pas de commission : son gain, ce sont les recettes moins
    // le loyer et les charges. C'est ce que dit la phrase.
    refuse_sur_chambre:
      "{chambre} est une chambre de la {maison}, en sous-location : il n'y a " +
      "pas de commission ici — ce qu'elle rapporte, c'est ses recettes moins " +
      "le loyer et les charges. Je n'ai rien préparé.",
    // ⛔ ET PAS SUR LA MAISON NON PLUS — LE RÉSIDU ARGENT (revue #1480, 2e
    // passe). Le serveur ACCEPTE un taux sur un parent (`portee:"parent"` ∧
    // pas de chambre visée, `logement-champ-handlers.js`) : rien ne l'arrêtait
    // en aval. Or poser 20 % sur la Maison Zen fait diverger deux moteurs —
    // `lib/proprio-handlers.js` hérite `parentLog.taux` sur les trois chambres
    // pendant que `src/lib/facturation-lignes.js` reste à 0 %. `l8` est à 0 %
    // DÉLIBÉRÉMENT : en sous-location, il n'y a pas de commission du tout.
    //
    // ⚠️ FAIL-CLOSED SUR TOUT PARENT, FAUTE DE MARQUEUR FIABLE. On refuse ici
    // dès qu'une maison a des chambres, sans distinguer sous-location et
    // mandat propriétaire — parce qu'AUCUN marqueur de sous-location n'est
    // lisible depuis `lireEnsembles` : il n'existe que `p6` en dur
    // (`lib/calc.js`, `SOUSLOC_LIDS`) et un test sur le NOM du propriétaire
    // (`/sous-loc/i`, operateur-handlers) — un lookup par nom, exactement ce
    // que ce dépôt refuse. Aujourd'hui les seuls ensembles du parc sont la
    // Maison Zen (p6, sous-location), donc la phrase dit vrai. Le jour où un
    // ensemble est en mandat propriétaire, la commission au parent redevient
    // légitime : il faudra alors un marqueur POSITIF (une colonne, pas un nom)
    // et une seconde phrase — jamais deviner.
    refuse_sur_parent:
      "La {maison} est en sous-location : il n'y a pas de commission ici, ni " +
      "sur la maison ni sur ses chambres — ce qu'elle rapporte, c'est ses " +
      "recettes moins le loyer et les charges. Je n'ai rien préparé.",
    // Le taux décide de ce que touche le propriétaire — mais rien ne part au
    // clic : c'est la PROCHAINE facture qui l'emploiera.
    ou_ca_part: ["atteignable"],
    sans_retour:
      "l'ancien taux n'est gardé nulle part : les factures déjà émises le gardent, mais la fiche, elle, l'oublie",
    ne_fait_pas:
      "il n'émet aucune facture et ne réécrit aucune facture déjà émise : si le mois en cours est déjà facturé pour ce logement, le geste est refusé. Un mois passé pas encore facturé, lui, prendra le nouveau taux — comme depuis l'écran.",
    dit_comme: [
      "Passe la commission de ce propriétaire à 18 %",
      "Change le taux que je prends sur ce logement",
      "Mets ce logement en commission fixe",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["argent", "sortant"],
    // ⚠️ ARGENT PROPRIO. Le serveur refuse (409 `mois_gele`) si une facture de
    // commission existe déjà pour le mois courant — garde qui n'existait
    // QU'à l'écran, et pas sur ce champ (relevé 2026-09-14). Une facture
    // émise est un snapshot : le passé ne se réécrit pas ; un mois passé NON
    // encore facturé prendra le nouveau taux, comme depuis l'écran.
    // E1 ✔ (un taux, refaisable) · E2 ✔ · E3 ✔ · E4 ✔ · E5 ✔.
  },

  // ══ LA PAGE PUBLIQUE D'UN LOGEMENT (2026-09-14) ══════════════════════════
  //
  // Dimitri : « publier / dépublier la page d'un logement (site public) ».
  //
  // ⚠️ L'ACTION N'EST PAS `site_content_upsert`, ET C'EST UNE DÉCISION PRISE
  // SUR LE CODE, PAS SUR LE NOM. La colonne visée (`statut_publication`) est
  // bien la sienne, mais cette action-là, relue en entier (`api/sites.js`) :
  //   · EXIGE le `slug` (400 sinon) — une adresse publique que le modèle ne
  //     connaît pas et ne doit pas composer ; un slug approché tomberait sur le
  //     409 d'un AUTRE logement, ou renommerait l'URL de celui-ci ;
  //   · RE-TRADUIT par Claude les champs dont le FR a bougé — publier ne doit
  //     rien coûter en jetons, et surtout ne doit pas pouvoir toucher un texte ;
  //   · RÉÉCRIT `auto_translation_hashes` et les marqueurs de retouche
  //     manuelle. Un geste qui annonce « publier » et remet ces marqueurs
  //     DÉBORDE son libellé — le critère même des interdits absolus.
  // D'où une action dédiée (`lib/site-publication-handlers.js`), qui LIT le
  // slug réel, refuse si la page n'existe pas, écrit `statut_publication` (+
  // `published_at` à la publication, comme l'écran) et déclenche la MÊME
  // revalidation fbz-sites — dans les deux sens, sinon une page dépubliée
  // resterait servie.
  //
  // ⚠️ VAGUE 4, PAS 1, ET LA DEMANDE DISAIT « vague 1 si strictement
  // réversible, sinon justifie ». Deux raisons, et la seconde est la vraie :
  //   1. `published_at` est ÉCRASÉ à chaque publication — la date de première
  //      mise en ligne n'est stockée nulle part ailleurs. Dépublier puis
  //      republier ne la rend pas. C'est ce que dit `sans_retour`.
  //   2. Surtout : CE GESTE SORT DE CHEZ NOUS. Publier rend une page
  //      accessible à n'importe qui sur Internet, et à l'indexation des
  //      moteurs de recherche ; dépublier retire une page qui a pu être
  //      partagée. Le geste se REFAIT, mais ce qui est sorti ne rentre pas —
  //      exactement la distinction que Dimitri a posée sur `pricing_unlock`
  //      entre « je peux annuler » et « je peux poser une autre valeur ».
  // La vague 1 est « la mécanique fait ses preuves » : elle n'est pas le lieu
  // d'un geste dont l'effet est public.
  site_page_publication_set: {
    // L'écran où Dimitri le constate : l'onglet Website de la fiche logement
    // (la fiche a deux onglets depuis ADR-195 — Info FBZ et Website).
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "site_page_publication_set",
    methode: "POST",
    args_autorises: ["logement_id", "statut"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    // Valeurs RECOPIÉES de la liste fermée du serveur, et un test vérifie
    // qu'elles lui sont égales. `archived`, qu'accepte `site_content_upsert`,
    // n'est PAS ici : ce geste publie ou dépublie ; archiver est une décision
    // de cycle de vie qu'on ne déduit pas d'une demande de dépublication.
    args_valeurs: { statut: ["published", "draft"] },
    apercu: {
      lecture: "site_page_publication_lire",
      depend_de: ["logement_id"],
      // ⚠️ `x-apercu-version`, PAS `x-cfg-version` — ce geste n'écrit pas le
      // blob de configuration, et un en-tête qui ment sur ce qu'il transporte
      // finit lu de travers. Le jeton est dérivé du SLUG et du STATUT : ce que
      // la carte annonce, mot pour mot. Si l'un des deux a bougé depuis
      // l'affichage, le serveur refuse (409) et renvoie le jeton frais.
      jeton: "x-apercu-version",
    },
    titre: "Publier ou dépublier la page publique",
    constatable:
      "l'état bascule dans Logements › fiche › Site, et la page part (ou est retirée) du site public : c'est la page que voient les voyageurs, ouverte à tout internet et trouvable sur Google — la carte montre son adresse et son état actuel avant le clic ; le geste est refusé si ce logement n'a pas encore de page, et la carte dit si le site public a bien été prévenu",
    sujet: "fiche",
    onglet: "site",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    ne_fait_pas:
      "il ne crée ni ne modifie AUCUN contenu de page, ne retraduit rien et ne coûte aucun jeton : il bascule un statut et redemande au site public de se régénérer. Une page qui n'existe pas encore fait refuser le geste.",
    dit_comme: [
      "Publie la page de ce logement sur mon site",
      "Retire cette page du site public",
      "Mets le Cocon en ligne",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    // Ça quitte FBZ : une page devient (ou cesse d'être) visible de tous, et
    // indexable. `sortant` est fait pour ça.
    marquages: ["sortant"],
    sans_retour:
      "Republier réécrit la date de mise en ligne, et ce qui a été vu ou indexé pendant la publication ne se retire pas.",
    // E1 ✔ (une colonne, rebasculable — rien n'est effacé) · E2 ✔ (le statut
    // change à l'écran, et la page publique suit) · E3 ✔ (écrit le MÊME couple
    // `statut_publication` + `published_at` que l'écran, ET déclenche la même
    // revalidation : écrire la colonne seule ferait un écrivain PARTIEL, la
    // page dirait « publiée » et le site servirait l'ancienne version) · E4 ✔
    // · E5 ✔.
  },
  // ══ INSCRIRE UN NOUVEAU PROPRIÉTAIRE — VAGUE 10, « LES FORMULAIRES » ══════
  //
  // Décision Dimitri (Q4 du cadrage) : les gestes de CRÉATION vont dans une
  // vague à eux, la 10, fermée par défaut. Rien de comparable n'existe dans
  // les vagues 1 à 9 — elles règlent, publient, répondent ; celle-ci fait
  // EXISTER quelque chose. Et une vague qui s'ouvre après vérification en prod
  // est le rythme que Dimitri a demandé dès le premier levier.
  //
  // ⚠️ IL N'EXISTE PAS DE LEVIER DE SUPPRESSION EN FACE, ET CE N'EST PAS UNE
  // ASYMÉTRIE GÊNANTE : l'écran sait retirer un propriétaire
  // (`removePropConfirmed`, et il le refuse tant qu'un logement lui reste
  // rattaché). Ce qui se crée ici se défait donc à la main, en deux clics, par
  // le chemin que Dimitri connaît — d'où `sans_retour: null`.
  //
  // ⚠️ `id` N'EST PAS DANS `args_autorises`. Il est tiré PAR LE SERVEUR
  // (`nextPropId`, max + 1 sur tout le parc) : un identifiant composé par un
  // modèle serait un doublon en puissance, et un doublon d'identifiant dans ce
  // KV, c'est une facture qui part au mauvais nom. Il n'est pas non plus
  // `args_imposes` : une valeur imposée est SCALAIRE et connue d'avance, or
  // celle-ci se calcule sur l'état de la base au moment de l'écriture.
  //
  // ⚠️ L'APERÇU : la carte lit `proprietaire_creer_lire` (avec le jeton de
  // Dimitri, jamais le modèle) et affiche l'identifiant pressenti, le nombre
  // de propriétaires déjà inscrits, et le doublon d'e-mail s'il y en a un.
  // Elle en reçoit le jeton de version, que l'écriture EXIGE (`x-cfg-version`,
  // 409 si la base a bougé) — le même mécanisme que le bouton Enregistrer.
  proprietaire_creer: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "proprietaire_creer",
    methode: "POST",
    args_autorises: [
      "type",
      "nom_famille",
      "prenom",
      "nom",
      "email",
      "telephone",
      "adresse",
      "code_postal",
      "ville",
    ],
    args_valeurs: { type: ["particulier", "societe"] },
    // Un nom, une rue, une ville : du texte que Dimitri DICTE, validé par le
    // registre des formulaires côté serveur (forme, longueur, e-mail) et
    // refusé s'il n'y entre pas. Aucun de ces champs n'est un montant.
    args_libres: [
      "nom_famille",
      "prenom",
      "nom",
      "email",
      "telephone",
      "adresse",
      "code_postal",
      "ville",
    ],
    apercu: {
      lecture: "proprietaire_creer_lire",
      depend_de: ["email"],
      jeton: "x-cfg-version",
    },
    titre: "Inscrire un nouveau propriétaire",
    constatable:
      "il apparaît dans Logements › Le parc, sans aucun logement rattaché ; ses coordonnées se relisent ensuite dans la feuille « Le propriétaire », en haut de l'onglet Infos de chacun de ses logements. La carte montre l'identifiant qui sera tiré et signale si son adresse e-mail est déjà celle d'un autre propriétaire.",
    // ⚠️ LE SUJET EST « fiche », ET C'EST L'ENDROIT OÙ DIMITRI LE CHERCHE.
    // Un propriétaire n'a pas d'écran à lui : sa feuille vit dans la fiche du
    // logement (`FicheInfosCtrl`, feuille « Le propriétaire »), et c'est là
    // qu'on répond à « tu sais faire quoi sur mes propriétaires ? ».
    // `argent` aurait été le voisin plausible — c'est bien lui qu'on facture —
    // mais ce sujet désigne les écrans Données / Facturation / Historique /
    // Compta, et il est déclaré SANS LEVIER dans `_forme.js` : y ranger
    // celui-ci rendrait cette déclaration fausse sans rien clarifier.
    sujet: "fiche",
    // Il entre dans le parc ; ses coordonnées se lisent dans Infos.
    onglet: "info",
    cout: ["rien"],
    // ⚠️ « personne » ET PAS « atteignable », ET LA NUANCE TIENT AU MOMENT.
    // Un propriétaire neuf n'a AUCUN logement : aucun contrat ne peut le
    // nommer, aucune facture ne peut le viser, aucun voyageur ne le croise.
    // Rien ne devient donc lisible dehors par ce clic. Le jour où un levier
    // MODIFIERA un propriétaire déjà rattaché — son adresse part alors dans
    // le prochain contrat — ce sera `atteignable`, et ce sera un autre levier.
    ou_ca_part: ["personne"],
    vague: 10,
    motif_requis: false,
    autonome: false,
    marquages: [],
    // Rien d'irréversible à signaler : l'écran sait le retirer.
    sans_retour: null,
    ne_fait_pas:
      "il ne lui ouvre AUCUN accès à l'espace propriétaire — ça se crée dans Réglages › Comptes, et ça demande un mot de passe. Il ne rattache aucun logement, ne pose aucune série de factures, et ne prévient personne : le propriétaire ignore qu'il a été inscrit.",
    dit_comme: [
      "Ajoute un nouveau propriétaire",
      "J'ai un nouveau proprio à inscrire",
      "Inscris Marie Dupont comme propriétaire",
    ],
    // E1 ✔ (création seule — rien n'est effacé, rien ne part chez un tiers,
    // aucun mouvement d'argent) · E2 ✔ (il est dans la liste du parc juste
    // après) · E3 ✔ (seul écrivain de `cfg.props[]` avec le bouton
    // Enregistrer, et celui-ci est protégé par le même jeton de version) ·
    // E4 ✔ (rien à attester : les neuf champs sont dictés par Dimitri) ·
    // E5 ✔.
  },

  // ══ INSCRIRE UN LOGEMENT AU PARC — VAGUE 10, « LES FORMULAIRES » ══════════
  //
  // Le second geste de CRÉATION, après `proprietaire_creer` et sur exactement
  // le même patron : identifiant tiré SERVEUR, jeton de version, diff intégral,
  // aucune ligne V2 amorcée.
  //
  // ⚠️ IL PORTE UN TAUX, DONC DE L'ARGENT — et c'est la seule différence de
  // fond avec son voisin. À l'écran, `addLog` pose `taux: 20` EN DUR : c'est
  // le seul endroit de l'application où un taux de commission apparaît sans
  // que personne l'ait dit. Dimitri le voit et le corrige ; un formulaire qui
  // le reprendrait le ferait passer pour un choix. Le taux est donc DICTÉ,
  // obligatoire, extrait par `lireNombreDicte` — et la carte le montre en
  // toutes lettres (`marquages: ["argent"]`).
  //
  // ⚠️ `sans_retour` N'EST PAS `null` ICI, CONTRAIREMENT AU PROPRIÉTAIRE, et
  // la différence n'est pas cosmétique. Un propriétaire sans logement se
  // retire en deux clics à l'écran. Un logement, NON : `logement_delete` est
  // un INTERDIT ABSOLU (il efface en cascade plus que son libellé n'annonce),
  // et la consigne de Dimitri du 2026-09-08 — « supprimer un logement, ne pas
  // le faire » — est toujours active. Ce qui est créé ici reste.
  //
  // ⚠️ UNE CHAMBRE DE SOUS-LOCATION N'EST PAS CE GESTE. `addSousLoc` pose deux
  // clés sous le parent de Maison Zen, sans commission propre (« la commission
  // se règle au niveau de la maison », l'écran le dit) : la moitié des
  // arguments ci-dessous n'y aurait aucun sens. Le serveur refuse d'ailleurs
  // un propriétaire porteur de la sous-location.
  logement_creer: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "logement_creer",
    methode: "POST",
    args_autorises: [
      "proprietaire_id",
      "nom",
      "statut",
      "commission_taux",
      "adresse_complete",
      "capacite_personnes",
      "nom_complet",
      "capacite_chambres",
      "capacite_salles_de_bain",
      "surface_m2",
    ],
    // ⚠️ SIX ARGUMENTS QUE LE SERVEUR EXIGE, DONC SIX ARGUMENTS DÉCLARÉS ICI.
    // Sans cette liste, `manqueIndispensable` ne juge rien : le bouton reste
    // cliquable, l'appel part, et le serveur répond 400 en NOMMANT ses champs
    // — « commission_taux (absent), adresse_complete (absent) ». C'est du
    // jargon sur une carte, et c'est exactement ce que la carte s'interdit.
    // Déclarés, ils sont glosés (`ARGS_LIBELLES`) et le refus se lit.
    args_indispensables: [
      "proprietaire_id",
      "nom",
      "statut",
      "commission_taux",
      "adresse_complete",
      "capacite_personnes",
    ],
    args_valeurs: { statut: ["active", "preparing", "inactive"] },
    // Un nom, une rue, un identifiant de propriétaire : ils ne se comparent à
    // aucune constante. `proprietaire_id` est vérifié par sa FORME
    // (`p<n>`) puis par son EXISTENCE dans la configuration lue — une liste
    // fermée écrite ici périmerait au prochain propriétaire inscrit.
    args_libres: [
      "proprietaire_id",
      "nom",
      "nom_complet",
      "adresse_complete",
      "commission_taux",
      "capacite_personnes",
      "capacite_chambres",
      "capacite_salles_de_bain",
      "surface_m2",
    ],
    apercu: {
      lecture: "logement_creer_lire",
      depend_de: ["proprietaire_id", "nom"],
      jeton: "x-cfg-version",
    },
    titre: "Inscrire un logement au parc",
    constatable:
      "il apparaît dans Logements › Le parc sous son propriétaire, avec son nom, son statut et son taux ; l'adresse et la capacité se relisent dans l'onglet Instructions de sa fiche. Il n'est raccordé à aucun calendrier et n'a ni prix, ni caution, ni code d'accès : la carte montre l'identifiant qui sera tiré et signale si le nom est déjà porté par un autre logement.",
    sujet: "fiche",
    onglet: "info",
    // Le taux décide de ce que touchera le propriétaire.
    cout: ["argent"],
    // ⚠️ « personne » ET PAS « atteignable », POUR LA RAISON EXACTE DU LEVIER
    // VOISIN : la nuance tient au MOMENT. Un logement neuf n'a aucune
    // réservation, aucun voyageur, aucune facture, et aucune page publiée — la
    // mise en ligne du site passe par `fbz_sites`, pas par ce statut. Rien ne
    // devient donc lisible dehors par ce clic. Le taux servira une facture À
    // VENIR, et c'est `cout: ["argent"]` qui le dit ; l'annoncer comme une
    // destination ferait croire qu'un propriétaire voit quelque chose
    // aujourd'hui.
    ou_ca_part: ["personne"],
    sans_retour:
      "Un logement inscrit ne se retire pas d'un clic : sa suppression effacerait en cascade ses réservations et ses pièces, et elle est tenue à l'écart de l'opérateur.",
    ne_fait_pas:
      "il ne le raccorde à AUCUN calendrier (ni iCal, ni Beds24), ne pose ni prix, ni plancher, ni caution, ni compte de paiement, ni code d'accès, ni photo, et ne publie aucune page. Il ne crée pas non plus de chambre de sous-location, et ne prévient personne.",
    dit_comme: [
      "J'ai un nouveau logement à rentrer",
      "Inscris un logement pour Pierre",
      "Ajoute un logement au parc",
    ],
    vague: 10,
    motif_requis: false,
    autonome: false,
    marquages: ["argent"],
    // E1 ✔ (création seule : rien n'est effacé, rien ne part chez un tiers,
    // aucun euro ne bouge — le taux décidera d'une facture à venir, et la
    // carte le dit) · E2 ✔ (il est dans le parc juste après) · E3 ✔ (seul
    // écrivain de ce chemin avec le bouton Enregistrer, protégé par le même
    // jeton de version) · E4 ✔ (rien à attester : tout est dicté par Dimitri)
    // · E5 ✔.
  },
});

// ══ LES BOOLÉENS DE CETTE FAMILLE, EN MOTS — `levier.argument` ════════════
//
// F5 (03/10/2026) : déplacées telles quelles de `lib/operateur-leviers.js`,
// qui les rassemble (la règle et ses gardes sont écrites là-bas, au-dessus de
// `NOMS_BOOLEENS`). Une phrase se déclare ICI, à côté de son levier, et nulle
// part ailleurs.
const BOOLEENS_LIBELLES = Object.freeze({
  "service_logement_toggle.actif": {
    oui: "Proposer le service",
    non: "Retirer le service",
  },
});

module.exports = { LEVIERS_LOGEMENTS, BOOLEENS_LIBELLES };
